import { useEffect, useState } from "react";
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircleFilled, CloseCircleFilled, DeleteFilled, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Popconfirm, Select, Space, Table, message } from "antd";
import { ColumnType, TablePaginationConfig } from "antd/es/table";

import DeviceApi from "../../api/DeviceApi";
import ProductApi from "../../api/ProductApi";
import KeyCodeUtils from "../../util/KeyCodeUtils";
import PermissionWrapper from "../../components/permission/PermissionWrapper";
import SceneDetails from "./components/scene-details/SceneDetails";
import { SceneManagementWrapper, FilterItem, FilterItemLabel, TableWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./SceneManagement.styled";
import { CreateSceneWizard } from "./components/create-scene-wizard/CreateSceneWizard";
import SceneApi from "../../api/SceneApi";

interface SceneManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
}

const SceneManagement: React.FC<SceneManagementProps> = (props)=> {
    // api
    let sceneApi = new SceneApi();
    let productApi = new ProductApi();
    let [messageApi, contextHolder] = message.useMessage();
    let navigate = useNavigate();
    
    // params
    let [searchParams] = useSearchParams();

    // state
    let [sceneViews, setSceneViews] = useState([]);
    let [openCreateSceneModal, setOpenCreateSceneModal] = useState(false);

    let [loading, setLoading] = useState(true);
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
        },
    });

    // events
    let viewSceneDetails = (sceneView: any)=> {
        navigate(`./scene/${sceneView.scene.id}`);
    };

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadSceneViews();
    };

    let onCreateSceneClick = ()=> {
        setOpenCreateSceneModal(true);
    };

    let onSearchKeyDown = (e)=> {
        if (e.keyCode == KeyCodeUtils.ENTER) {
            loadSceneViews();
        }
    };

    let onDeleteScene = async (sceneView: any)=> {
        await sceneApi.delete(sceneView.scene.id);
        messageApi.open({
            type: 'success',
            content: '删除设备成功',
          });
        loadSceneViews();
    };

    // load device views
    let loadSceneViews = async ()=> {
        // loading animation
        setLoading(true);
        let deviceViewsPage = (await sceneApi.queryViews({
            search: search,
            pageNo: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
        })).data.data;

        let newTableParams = {pagination: {...tableParams.pagination}};
        newTableParams.pagination.total = deviceViewsPage.totalElements;
        setTableParams(newTableParams);
        setSceneViews(deviceViewsPage.elements);
        setLoading(false);
    };

    // column definition
    let columns: ColumnType<any>[] = [
        /* {
            title: '状态',
            dataIndex: 'scene.status',
            key: "scene.status",
            align: 'center',
            render: (text, record, index)=> {
                return record.scene.status;
            }
        }, */
        {
            title: '场景名称',
            dataIndex: 'scene.name',
            key: 'scene.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> viewSceneDetails(record)}>{record.scene.name}</a>
            }
        },
        /*
        {
            title: '设备数量',
            dataIndex: 'scene.deviceCount',
            key: 'scene.deviceCount',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.scene.devices.length}</span>
            }
        },
        {
            title: '变量数量',
            dataIndex: 'scene.propertyCount',
            key: 'scene.propertyCount',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record.scene.properties.length}</span>
            }
        },
        */
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewSceneDetails(record)}>查看详情</a>
                    <PermissionWrapper permissionCodePath="device-management.device.delete-device">
                        <Popconfirm
                                title="删除场景"
                                description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将删除场景及相关数据，删除的数据无法恢复，确认要删除么?</div> }
                                onConfirm={()=> onDeleteScene(record)}
                                okText="确定"
                                cancelText="取消"
                            > 
                            <a>删除场景</a>
                        </Popconfirm>
                    </PermissionWrapper>
                </Space>
            }
        }
    ];

    // effect
    useEffect(()=> {
        loadSceneViews();
    }, []);

    useEffect(()=> {
        loadSceneViews();
    }, [search]);
    
    // render
    return (
        <SceneManagementWrapper>
            {contextHolder}
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <PermissionWrapper permissionCodePath="scene-management.scene.create-scene">
                            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateSceneClick}>新增场景</Button>
                        </PermissionWrapper>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <Space size="middle">
                        <FilterItem>
                            <FilterItemLabel>搜索</FilterItemLabel>
                            <Input 
                                placeholder="请输入关键字" 
                                suffix={<SearchOutlined />} 
                                size="middle" 
                                value={search} 
                                onChange={(e)=> setSearch(e.target.value)} 
                                onKeyDown={(e)=> onSearchKeyDown(e)}
                            />
                        </FilterItem>
                    </Space>
                </ToolBarRightWrapper>

                <Modal open={openCreateSceneModal} 
                    onClose={()=> setOpenCreateSceneModal(false)} 
                    footer={null} 
                    closable={false}
                    destroyOnClose={true}
                    width={600}
                    height={700}
                    >
                    <CreateSceneWizard onClose={()=> setOpenCreateSceneModal(false)} onComplete={()=> loadSceneViews()}/>
                </Modal>
            </ToolBarWrapper>
            <TableWrapper>
                <Table 
                    rowKey={(record)=> record.scene.id}
                    columns={columns} 
                    dataSource={sceneViews}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={onTableChange}
                />
            </TableWrapper>

            <Routes>
                <Route path="scene/:sceneId" element={<SceneDetails onClose={()=>{}} onComplete={()=>{}}/>} />
            </Routes>
        </SceneManagementWrapper>
    );
}

export default SceneManagement;
